import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ILoginRequest, ILoginResponse, IRefreshTokenRequest } from '../interfaces/auth.interface';
import { GeneralResponse } from '../interfaces/generic-response.interface';
import { ResourceService } from './resource.service';
import { IUserResponse } from "../interfaces/user.interface";

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ResourceService<
  ILoginResponse | any
> {
  private readonly endPoint = '/auth/login';
  public token: string | null = null;
  public tokenExpiresAt: Date | null = null;
  public refreshToken: string | null = null;
  public user: IUserResponse | null = null;
  public dashboardData: any;

  hasToken() {
    return !!this.token;
  }

  login(
    data: ILoginRequest
  ): Observable<GeneralResponse<ILoginResponse>> {
    this.removeCompany();
    const url = `/${data.company}/en/backoffice${this.endPoint}`;
    const body = {
      email: data.email,
      password: data.password,
      remember: false
    };
    return this.post(url, body);
  }

  successLogin(login: ILoginResponse, company: string) {
    this.saveAuthSession(login);
    if (company) {
      localStorage.setItem('company', company);
    }
    if (login.user) {
      this.setUser(login.user);
    }
  }

  saveAuthSession(login: Partial<ILoginResponse> | null | undefined) {
    if (!login) {
      return;
    }

    const accessToken = this.normalizeStoredToken(login.token);
    if (accessToken) {
      this.token = accessToken;
      localStorage.setItem('token', accessToken);
    }

    const refreshToken = this.extractRefreshToken(login);
    if (refreshToken) {
      this.refreshToken = refreshToken;
      localStorage.setItem('refreshToken', refreshToken);
    }

    if (login.token_expires_at) {
      const expiresValue =
        login.token_expires_at instanceof Date
          ? login.token_expires_at.toISOString()
          : String(login.token_expires_at);
      if (this.normalizeStoredToken(expiresValue)) {
        this.tokenExpiresAt = new Date(expiresValue);
        localStorage.setItem('tokenExpiresAt', expiresValue);
      }
    }
  }

  getRefreshToken(): string | null {
    const refreshToken = this.normalizeStoredToken(
      localStorage.getItem('refreshToken')
    );
    if (!refreshToken) {
      const raw = localStorage.getItem('refreshToken');
      if (raw === 'undefined' || raw === 'null') {
        localStorage.removeItem('refreshToken');
      }
      this.refreshToken = null;
      return null;
    }

    this.refreshToken = refreshToken;
    return refreshToken;
  }

  setUser(user: IUserResponse) {
    this.user = user;
  }

  logout() {
    this.token = null;
    this.refreshToken = null;
    this.tokenExpiresAt = null;
    this.user = null;
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiresAt');
  }

  refreshUserToken(): Observable<GeneralResponse<ILoginResponse>> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('Refresh token is missing'));
    }

    const body: IRefreshTokenRequest = { token: refreshToken };
    return this.post('/auth/refresh-token', body);
  }

  private extractRefreshToken(
    login: Partial<ILoginResponse>
  ): string | null {
    const record = login as Partial<ILoginResponse> & {
      refreshToken?: string;
    };
    return (
      this.normalizeStoredToken(record.refresh_token) ??
      this.normalizeStoredToken(record.refreshToken)
    );
  }

  private normalizeStoredToken(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
      return null;
    }

    if (trimmed.startsWith('"')) {
      try {
        return this.normalizeStoredToken(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }

    return trimmed;
  }

}
